import { createAsyncThunk } from '@reduxjs/toolkit';
import FilesServiceResource from 'api/service/FilesResource';
import PropertyServiceResource from 'api/service/PropertiesResource';
import PropertiesResource from 'api/service/PropertiesResource';

export const listFiles = createAsyncThunk('files/listFiles', async (initialData, { rejectWithValue }) => {
  const filesServiceResource = new FilesServiceResource();
  const propertyServiceResource = new PropertyServiceResource();
  const { search, scope, parent, property, sort = 'last_modified asc', content_types, size = 15 } = initialData;
  const params = {
    size,
    sort,
    offset: 0,
    includes: 'parent',
    ...(search && { search }),
    ...(content_types && { content_types }),
  };

  const response = search
    ? await filesServiceResource.search(params)
    : property
      ? await propertyServiceResource.listFiles(property, params)
      : parent
        ? await filesServiceResource.listChildren(parent, params)
        : scope
          ? await filesServiceResource.listRoot(scope, params)
          : null;

  if (response?.status === 200) {
    return { data: response?.data || [], offset: 15, hasMore: response?.data?.length > 0 };
  }
  return rejectWithValue('Files not found');
});

export const listFilesNext = createAsyncThunk('files/listFilesNext', async (initialData, { rejectWithValue }) => {
  const filesServiceResource = new FilesServiceResource();
  const propertyServiceResource = new PropertyServiceResource();
  const {
    search,
    scope,
    parent,
    property,
    sort = 'last_modified asc',
    content_types,
    size = 15,
    offset = 0,
  } = initialData;
  const params = {
    size,
    sort,
    offset,
    includes: 'parent',
    ...(search && { search }),
    ...(content_types && { content_types }),
  };

  const response = search
    ? await filesServiceResource.search(params)
    : property
      ? await propertyServiceResource.listFiles(property, params)
      : parent
        ? await filesServiceResource.listChildren(parent, params)
        : scope
          ? await filesServiceResource.listRoot(scope, params)
          : null;

  if (response?.status === 200) {
    return { data: response?.data || [], offset: offset + 15, hasMore: response?.data?.length > 0 };
  }
  return rejectWithValue('Files not found');
});

export const listShareBuckets = createAsyncThunk('files/listShareBuckets', async (initialData, { rejectWithValue }) => {
  const { storeBucketOnly = false } = initialData;

  const filesServiceResource = new FilesServiceResource();
  const { status, data } = await filesServiceResource.listShareBuckets();
  if (status === 200) {
    return { data: data || [], storeBucketOnly };
  }
  return rejectWithValue('Files not found');
});

export const listSharedFiles = createAsyncThunk('files/listSharedFiles', async (initialData, { rejectWithValue }) => {
  const filesServiceResource = new FilesServiceResource();
  const { scope, orgId, parent, sort = 'last_modified asc' } = initialData;
  const params = { account: orgId, size: 20, sort, offset: 0 };

  try {
    const response = parent
      ? await filesServiceResource.listChildren(parent, params)
      : scope
        ? await filesServiceResource.listRoot(scope, params)
        : null;

    if (response?.status === 200) {
      return { data: response?.data || [], offset: 15 };
    }
    return rejectWithValue('Files not found');
  } catch (error) {
    return rejectWithValue(error.message || 'An error occurred');
  }
});

export const listSharedFilesNext = createAsyncThunk(
  'files/listSharedFilesNext',
  async (initialData, { rejectWithValue }) => {
    const filesServiceResource = new FilesServiceResource();
    const { scope, orgId, parent, sort = 'last_modified asc', offset = 0 } = initialData;
    const params = { account: orgId, size: 15, sort, offset };

    try {
      const response = parent
        ? await filesServiceResource.listChildren(parent, params)
        : scope
          ? await filesServiceResource.listRoot(scope, params)
          : null;

      if (response?.status === 200) {
        return { data: response?.data || [], offset: offset + 15 };
      }
      return rejectWithValue('Files not found');
    } catch (error) {
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

export const listPropertyFolderFiles = createAsyncThunk(
  'files/listPropertyFolderFiles',
  async (initialData, { rejectWithValue }) => {
    const propertiesResource = new PropertiesResource();
    const { parent, sort = 'last_modified asc', size = 15 } = initialData;
    const params = { size, sort, offset: 0, includes: 'parent' };

    const { status, data } = await propertiesResource.property(parent).files(params);
    if (status === 200) {
      return { data: data || [], offset: 15 };
    }
    return rejectWithValue('Files not found');
  }
);

export const listPropertyFolderFilesNext = createAsyncThunk(
  'files/listPropertyFolderFilesNext',
  async (initialData, { rejectWithValue }) => {
    const propertiesResource = new PropertiesResource();
    const { parent, sort = 'last_modified asc', offset = 0 } = initialData;
    const params = { size: 15, sort, offset, includes: 'parent' };

    const { status, data } = await propertiesResource.property(parent).files(params);
    if (status === 200) {
      return { data: data || [], offset: offset + 15 };
    }
    return rejectWithValue('Files not found');
  }
);

/* bin page */
export const listTrashFiles = createAsyncThunk('files/listTrashFiles', async (initialData, { rejectWithValue }) => {
  const filesServiceResource = new FilesServiceResource();
  const { sort = 'last_modified asc' } = initialData;
  const params = { size: 15, sort, offset: 0 };
  const response = await filesServiceResource.listBin(params);
  if (response?.status === 200) {
    return { data: response?.data || [], offset: 15 };
  }
  return rejectWithValue('Files not found');
});

export const listTrashFilesNext = createAsyncThunk(
  'files/listTrashFilesNext',
  async (initialData, { rejectWithValue }) => {
    const filesServiceResource = new FilesServiceResource();
    const { offset = 0, sort = 'last_modified asc' } = initialData;
    const params = { size: 15, sort, offset };
    const response = await filesServiceResource.listBin(params);
    if (response?.status === 200) {
      return { data: response?.data || [], offset: offset + 15 };
    }
    return rejectWithValue('Files not found');
  }
);

export const restoreTrashFile = createAsyncThunk('files/restoreTrashFile', async (initialData, { rejectWithValue }) => {
  const filesServiceResource = new FilesServiceResource();
  const id = initialData;
  const params = { includes: 'parent' };
  const response = await filesServiceResource.file(id).restore(params);
  if (response.status === 200) {
    return { data: response?.data || {} };
  }
  return rejectWithValue('File not found');
});
